import type { CardBase, KanbanProps } from "./interfaces";
import {
    DragDropContext,
    Draggable,
    Droppable,
    OnDragEndResponder,
} from "react-beautiful-dnd";
import CardsIcon from "~/assets/cards-count.svg";
import cn from "classnames";
import { useSignal } from "@preact/signals";

const Kanban = <Card extends CardBase, Group extends string>({
    groups,
    renderCard,
    onChange,
    className,
    groupClassName,
}: KanbanProps<Card, Group>) => {
    const isDragging = useSignal(false);

    const handleDragEnd: OnDragEndResponder = (result) => {
        const sourceCard = groups
            .find((group) => group.key === result.source.droppableId)
            ?.cards.find((card) => card.id === result.draggableId);
        const targetGroup = result.destination?.droppableId;
        const targetIndex = result.destination?.index || 0;

        if (sourceCard && targetGroup) {
            onChange(sourceCard, targetGroup as Group, targetIndex);
        }

        isDragging.value = false;
    };

    return (
        <DragDropContext
            onDragStart={() => (isDragging.value = true)}
            onDragEnd={handleDragEnd}
        >
            <div className={cn("flex", className && String(className))}>
                {groups.map(({ key, cards }) => (
                    <div
                        key={key}
                        className={cn(
                            "flex flex-col w-full h-full mr-4 border-gray-200 border-x last:mr-0",
                            groupClassName
                        )}
                    >
                        <h3 className="sticky top-0 flex items-center justify-between px-4 py-2 font-bold text-center text-white capitalize rounded-t-lg shadow-inner bg-sky-800">
                            <span>{key}</span>
                            <div className="flex items-center">
                                <CardsIcon />
                                <span className="ml-2">{cards.length}</span>
                            </div>
                        </h3>
                        <Droppable droppableId={key}>
                            {(provided, snapshot) => (
                                <div
                                    className={cn(
                                        "h-full border-2 pt-3 px-3 border-dashed border-transparent transition duration-100",
                                        isDragging.value && "border-sky-800",
                                        snapshot.isDraggingOver
                                            ? "bg-sky-100"
                                            : "bg-gray-100"
                                    )}
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                >
                                    {cards.map((card, index) => (
                                        <Draggable
                                            key={card.id}
                                            draggableId={card.id}
                                            index={index}
                                        >
                                            {(
                                                {
                                                    innerRef,
                                                    dragHandleProps,
                                                    draggableProps: {
                                                        style,
                                                        ...rest
                                                    },
                                                },
                                                snapshot
                                            ) => (
                                                <div
                                                    className="pb-2"
                                                    ref={innerRef}
                                                    {...(dragHandleProps as any)}
                                                    {...rest}
                                                    style={{ ...style }}
                                                >
                                                    {renderCard(
                                                        card,
                                                        snapshot.isDragging,
                                                        key,
                                                        index
                                                    )}
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </div>
                ))}
            </div>
        </DragDropContext>
    );
};

export default Kanban;
